import React, { createContext, useContext, useMemo } from 'react';

import type { Localization } from '@mui/material/locale';
import type { enUS as DataGridLocaleEnUS } from '@mui/x-data-grid-pro/locales';
import type {
  enUS as DatePickerLocaleEnUS,
  DEFAULT_LOCALE,
} from '@mui/x-date-pickers-pro/locales';

import type { SupportedLocale } from './locale-mapper';
import { isSupportedLocale, localeMapper } from './locale-mapper';

export type ThemeLocalization = {
  core: Localization;
  dataGrid: typeof DataGridLocaleEnUS;
  datePicker: typeof DEFAULT_LOCALE | typeof DatePickerLocaleEnUS;
};

export interface LocaleContextValue {
  dayJsLocale: SupportedLocale;
  locale: SupportedLocale;
  localization: ThemeLocalization;
}

const LocaleContext = createContext<LocaleContextValue | null>(null);

export const useMuiLocale = (): LocaleContextValue => {
  const value = useContext(LocaleContext);

  if (value === null) {
    throw new Error(
      'useLocale must be used within the context of LocaleProvider.',
    );
  }

  return value;
};

type MuiLocaleProviderProps = React.PropsWithChildren<{ locale: string }>;

export const MuiLocaleProvider = ({
  children,
  locale,
}: MuiLocaleProviderProps) => {
  const value = useMemo((): LocaleContextValue => {
    const supportedLocale = isSupportedLocale(locale) ? locale : 'en';
    const locales = localeMapper[`${supportedLocale}`] ?? localeMapper.en;
    return {
      dayJsLocale: locales.dayJsAdapter,
      locale: supportedLocale,
      localization: {
        core: locales.core,
        dataGrid: locales.dataGrid,
        datePicker: locales.datePicker,
      },
    };
  }, [locale]);

  return (
    <LocaleContext.Provider value={value}>{children}</LocaleContext.Provider>
  );
};
